/**
 * When the socket should be connected, and which trading session a price
 * belongs to.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THREE WINDOWS, NOT ONE
 *
 * They are easy to conflate and they answer different questions:
 *
 *   CONNECT   09:15–16:00  Should a socket exist at all? Outside this we do
 *                          not connect, do not retry, and do not log about it.
 *                          The gateway is shut and knocking is noise for us
 *                          and harassment for them.
 *
 *   TRADING   09:15–15:30  Is SILENCE suspicious? A socket receiving nothing
 *                          at 15:45 is not broken — the market shut. Only
 *                          inside this does the watchdog tear a socket down.
 *
 *   SESSION   the IST DATE a price belongs to. Not a clock at all; see below.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE HOLIDAY PROBLEM, AND WHY NOTHING HERE RESETS ON A TIMER
 *
 * The obvious design for a last-close cache is "clear it every morning at
 * 09:15". That is wrong, and wrong in the worst direction: on Diwali the
 * cache empties, no session ever starts to refill it, and every screen shows
 * blanks all day for a market that simply is not open.
 *
 * Hardcoding a holiday calendar is also wrong — it is a list that goes stale,
 * and being stale means being wrong on exactly the day it matters.
 *
 * So nothing is reset. A cached price is STAMPED with the trading session it
 * came from, and a cache entry is superseded when a LATER session has
 * actually produced ticks. The feed is the evidence:
 *
 *   normal day   first tick at 09:15 -> session becomes today -> yesterday's
 *                entries are superseded and refetched as the day closes
 *   holiday      no ticks all day -> the session marker does not move ->
 *                Friday's closes stay valid and keep being served, which is
 *                exactly right because Friday's close IS the last price
 *   half day     closes early; the session still advanced, so its closes are
 *                today's. No special case
 *   long weekend works for the same reason a holiday does
 *
 * The calendar is never consulted because the market tells us. That is the
 * whole idea.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { istParts, isWeekendIst } from './istClock.js';
/**
 * When a socket should be held. Minutes from IST midnight.
 *
 * TEN MINUTES PAST THE CLOSE, not thirty. The exchange stops at 15:30 and the
 * socket is deliberately held past it, because a square-off confirmation or a
 * late fill can still arrive after the bell and a feed that is already down
 * cannot carry it. What it does NOT need to do is sit open until 16:00: by
 * 15:40 the trade book has settled, and the remaining twenty minutes were
 * holding a streaming session — of which there is exactly one per app key
 * ([B14]) — for nothing.
 *
 * Changed from 16:00 on 2026-10-14. If a confirmation is ever seen arriving
 * after 15:40, this is the number to move, and `isTradingWindow` below is
 * deliberately NOT the same value: silence between 15:30 and here is normal,
 * not a fault.
 */
export const CONNECT_WINDOW_IST = {
    openMin: 9 * 60 + 15,
    closeMin: 15 * 60 + 40,
};
/** When silence means something is broken. The exchange's own hours. */
export const TRADING_WINDOW_IST = {
    openMin: 9 * 60 + 15,
    closeMin: 15 * 60 + 40,
};
/**
 * When an order may be SENT — ROADMAP 9.38.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A THIRD WINDOW, BECAUSE IT ANSWERS A THIRD QUESTION
 *
 * The order path used `TRADING_WINDOW_IST`, whose own line says what it is
 * for: "when silence means something is broken". Those are not the same
 * question. One is about our feed's health, the other about whether the
 * exchange will take an order — and borrowing the first for the second means
 * a change to either drags the other with it. The file already makes this
 * split once, between CONNECT and TRADING, with the note that they are
 * "deliberately NOT the same value".
 *
 * SIXTEEN HUNDRED, at the owner's instruction on 2026-11-03: NIFTY now runs
 * past 15:30, and a barrier that refuses at 15:31 refuses real trading. The
 * extra margin past the bell is deliberate and costs nothing — an order the
 * exchange will not take is rejected by the exchange, with its own reason,
 * which is a better answer than ours guessing the minute.
 *
 * It is NOT a claim about when the market closes. That claim lives in
 * `TRADING_WINDOW_IST` and is still 15:30, because moving it would tell the
 * feed watchdog that silence at 15:45 is a fault.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export const ORDER_WINDOW_IST = {
    openMin: 9 * 60 + 15,
    closeMin: 16 * 60,
};
const minutesIst = (at) => {
    const p = istParts(at);
    return p.h * 60 + p.min;
};
/**
 * Should the feed hold a socket right now?
 *
 * Weekends are out. Exchange holidays are NOT excluded, and deliberately so:
 * we do not have a calendar, and a socket that connects on a holiday simply
 * sits quiet and costs nothing. Guessing a holiday wrong in the other
 * direction — refusing to connect on a day the market IS open — would be
 * a silent outage.
 */
export function isConnectWindow(at = new Date()) {
    if (isWeekendIst(at))
        return false;
    const m = minutesIst(at);
    return m >= CONNECT_WINDOW_IST.openMin && m < CONNECT_WINDOW_IST.closeMin;
}
/** Is a quiet socket suspicious right now? */
export function isTradingWindow(at = new Date()) {
    if (isWeekendIst(at))
        return false;
    const m = minutesIst(at);
    return m >= TRADING_WINDOW_IST.openMin && m <= TRADING_WINDOW_IST.closeMin;
}
/**
 * May an order be sent right now? — 9.38.
 *
 * Weekends are out, holidays are not: we carry no calendar, and refusing on
 * a day the market IS open would be worse than letting the exchange reject
 * an order on a day it is not. The same trade `isConnectWindow` makes.
 */
export function isOrderWindow(at = new Date()) {
    if (isWeekendIst(at))
        return false;
    const m = minutesIst(at);
    return m >= ORDER_WINDOW_IST.openMin && m <= ORDER_WINDOW_IST.closeMin;
}
/** "09:15 and 16:00" — the window, for a refusal that has to name it. */
export const orderWindowLabel = () => `${String(Math.floor(ORDER_WINDOW_IST.openMin / 60)).padStart(2, '0')}:` +
    `${String(ORDER_WINDOW_IST.openMin % 60).padStart(2, '0')} and ` +
    `${String(Math.floor(ORDER_WINDOW_IST.closeMin / 60)).padStart(2, '0')}:` +
    `${String(ORDER_WINDOW_IST.closeMin % 60).padStart(2, '0')}`;
/**
 * Milliseconds until the connect window next opens.
 *
 * Used instead of a retry backoff outside the window: there is nothing to
 * back off from, there is a time to wake up.
 */
export function msUntilConnectWindow(at = new Date()) {
    /*
     * ─────────────────────────────────────────────────────────────────────────
     * ALREADY OPEN MEANS ZERO, NOT TOMORROW
     *
     * This returned a full day whenever the clock was past 09:15, which reads
     * as "the open has been and gone". Inside the window that is false: the
     * window IS open, and the next moment one may connect is now.
     *
     * It cost a trading day in production. The feed's wake timer is set to
     * land exactly on 09:15:00.000, so on 3 Nov 2026 the callback ran a
     * fraction early, `isConnectWindow` said "not yet" and sent the session
     * back to sleep — and this function, reading the clock a millisecond
     * later on the far side of the boundary, answered 1440 minutes. The
     * socket slept through the session with the market open, and because a
     * pending timer blocks every reconnect path, nothing could wake it.
     *
     * Two clock reads either side of one millisecond, and the timer was
     * aimed at precisely that millisecond — so this was the expected outcome
     * of a near-miss, not a freak.
     * ─────────────────────────────────────────────────────────────────────────
     */
    if (isConnectWindow(at))
        return 0;
    const p = istParts(at);
    const m = p.h * 60 + p.min;
    let days = 0;
    let target = CONNECT_WINDOW_IST.openMin;
    if (m >= CONNECT_WINDOW_IST.openMin) {
        // Past today's close — the next open is tomorrow.
        days = 1;
    }
    // Skip the weekend. Friday evening waits until Monday rather than waking
    // twice for nothing.
    let weekday = (p.weekday + days) % 7;
    while (weekday === 0 || weekday === 6) {
        days += 1;
        weekday = (weekday + 1) % 7;
    }
    const minutesAway = days * 24 * 60 + target - m;
    return minutesAway * 60_000 - at.getSeconds() * 1000 - at.getMilliseconds();
}
/**
 * The IST calendar date, `YYYY-MM-DD`.
 *
 * The key a cached price is filed under. Derived from the price's own
 * exchange timestamp wherever possible, so an entry describes the session it
 * came from rather than the moment we happened to store it — a quote fetched
 * at 23:00 for a contract that last traded at 15:29 belongs to TODAY's
 * session, not to tonight.
 */
export function istDate(at = new Date()) {
    const p = istParts(at);
    return `${p.y}-${String(p.m).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}
/** Same, from milliseconds since the Unix epoch. */
export const istDateOf = (ms) => istDate(new Date(ms));
/**
 * Is a cached entry superseded?
 *
 * True only when a LATER session has actually produced ticks. An entry from
 * the current session is current; an entry from before it is not. Where no
 * session has ever been observed — a fresh install, or a weekend after a
 * deploy — nothing is superseded, because there is no evidence that anything
 * newer exists.
 */
export function isSuperseded(entrySession, latestSession) {
    if (!latestSession)
        return false;
    return entrySession < latestSession;
}
/**
 * Could a price with this timestamp have come from a real session?
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WEEKENDS ONLY. NO HOLIDAY CALENDAR, DELIBERATELY.
 *
 * Measured on the live gateway, Saturday 2026-10-17: `/instruments/quotes`
 * answered with timestamps from THAT MORNING — a NIFTY 23,000 CE reporting
 * `PercentChange: 1172.65`, a range of 69.9 to 1954.8, and call prices that
 * rose as the strike rose. A broker's test session, served on the same
 * endpoint and in the same shape as real data.
 *
 * It cannot be told apart by looking at the numbers: an option genuinely can
 * move 1000% in a day, and any plausibility rule strict enough to catch this
 * would eventually throw away a real print on a violent morning. The one
 * thing that IS certain is the calendar — no exchange trades on a Saturday —
 * and that needs no list that goes stale.
 *
 * A HOLIDAY is deliberately not covered. Guessing one wrong in this
 * direction discards a genuine price, and `isSuperseded` already keeps a
 * holiday from advancing the session marker (see this file's header).
 *
 * Timestamps at or below zero return TRUE: "no timestamp" is a different
 * problem from "a timestamp that cannot be real", and the caller that cares
 * about absence checks for it separately.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function isPlausibleSessionTime(exchTsMs) {
    if (!(exchTsMs > 0))
        return true;
    return !isWeekendIst(new Date(exchTsMs));
}
//# sourceMappingURL=marketWindow.js.map